import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import "./View.css";
import { useParams, NavLink } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { WorkOrderDocument } from "./Print";

const usePermissions = () => {
  const authData = useMemo(() => {
    try {
      const userDetailsString = localStorage.getItem("zTaskUserDetails");
      if (!userDetailsString) return { user: null, permissions: [] };
      const userDetails = JSON.parse(userDetailsString);
      const permissions = userDetails?.role?.permissions
        ? userDetails.role.permissions.map((p) => p.title)
        : [];
      return { user: userDetails, permissions };
    } catch (error) {
      console.error("Failed to parse user details from localStorage:", error);
      return { user: null, permissions: [] };
    }
  }, []);

  const hasPermission = useCallback(
    (requiredPermission) => {
      if (Array.isArray(requiredPermission)) {
        return requiredPermission.some((p) => authData.permissions.includes(p));
      }
      return authData.permissions.includes(requiredPermission);
    },
    [authData]
  );

  return { user: authData.user, hasPermission };
};

function WorkOrderView() {
  const { id } = useParams();
  const ref = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [allData, setAllData] = useState(null);
  const token = localStorage.getItem("zTaskAuthToken");
  const { user, hasPermission } = usePermissions();

  useEffect(() => {
    setIsLoading(true);
    if (
      !hasPermission("VIEW WORK ORDER") &&
      !hasPermission("VIEW ALL WORK ORDERS")
    ) {
      setError(
        "Access Denied: You do not have permission to view work orders."
      );
      return;
    }
    const fetchCompany = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/get-company`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setCompany(data.message[0]);
      }
    };
    const fetchWorkOrder = async () => {
      await fetchCompany();
      const res = await fetch(
        `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders/${id}/report`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setAllData(data);
      }
    };
    fetchWorkOrder().finally(() => {
      setIsLoading(false);
    });
  }, [id, token, hasPermission, user]);

  const validTransactions = useMemo(() => {
    if (!allData?.work_order_tran) return [];
    return allData.work_order_tran.filter(
      tran => tran !== null && tran !== undefined && typeof tran === 'object'
    );
  }, [allData]);

  // Calculate grand total - simply sum all transaction totals (tax already included)
  const grandTotal = useMemo(() => {
    if (!validTransactions.length) return 0;
    
    return validTransactions.reduce((sum, tran) => {
      return sum + parseFloat(tran.total_amount || 0);
    }, 0);
  }, [validTransactions]);

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (isLoading)
    return <div className="loading-message">Loading work order details...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!allData || !company)
    return (
      <div className="error-message">
        Error: Work Order data could not be fully loaded. Please try again.
      </div>
    );

  return (
    <div className="work-order-view-page">
      <header className="page-header">
        <div className="download-button-div">
          <NavLink to="/work-orders">
            <button className="backbutton">Back To List</button>
          </NavLink>
          {hasPermission("DOWNLOAD WORK ORDER PDF") && (
            <PDFDownloadLink
              document={
                <WorkOrderDocument allData={allData} company={company} />
              }
              fileName={`work-order-${allData.work_order_master.wo_no}.pdf`}
              className="download-button"
            >
              {({ loading }) =>
                loading ? "Loading document..." : "Download PDF"
              }
            </PDFDownloadLink>
          )}
        </div>
      </header>

      <div className="work-order-container" ref={ref}>
        {/* Header Section */}
        <div className="header-section">
          <div className="company-info">
            <div className="company-name">{company.companyName}</div>
            <div className="company-details">
              <strong>Address:</strong> <span>{company.address}</span>
              <br />
              <strong>Phone:</strong> <span>{company.telephoneNumber}</span>
              <br />
              <strong>Website:</strong> {company.websiteUrl}
            </div>
          </div>
          <div className="work-order-info">
            <div className="work-order-title">WORK ORDER</div>
            <div className="wo-number-section">
              <div className="wo-label">WORK ORDER #</div>
              <div className="wo-value">
                {allData.work_order_master.wo_no || "00-0000"}
              </div>
            </div>
            <div className="wo-date-section">
              <div className="wo-label">DATE</div>
              <div className="wo-value">
                {allData.work_order_master.wo_date
                  ? new Date(
                      allData.work_order_master.wo_date
                    ).toLocaleDateString()
                  : "12/22/2010"}
              </div>
            </div>
          </div>
        </div>

        {/* Bill To and Job Details Section */}
        <div className="bill-to-section">
          <div className="bill-to-header">CUSTOMER DETAILS</div>
          <div className="bill-to-header">PROJECT DETAILS</div>
        </div>
        <div className="bill-to-section">
          <div className="bill-to-content">
            <strong>Name: </strong>
            {allData.customers?.name || "Name"}
            <br />
            <strong>Address: </strong>
            {allData.customers?.address || "Street Address"}
            <br />
            <strong>City: </strong>
            {allData.customers?.city || "City"}, {allData.customers.state_name}
            <br />
            <strong>Pincode: </strong>
            {allData.customers?.pincode}
            <br />
            <strong>Vendor Code: </strong>
            {allData.customers?.vendor_code || "N/A"} <br />
            <strong>Contact details: </strong>
            {allData.customers?.contact_details}
            <br />
            <strong>GST No: </strong>
            {allData.customers?.gst_no || "N/A"}
            <br />
            <strong>PAN No: </strong>
            {allData.customers?.pan_no || "N/A"}
          </div>
          <div className="job-details-content">
            <strong>Name: </strong>
            {allData.projects.project_name}
            <br />
            <strong>Address: </strong>
            {allData.projects.address}
            <br />
            <strong>City: </strong>
            {allData.projects.city}, {allData.projects.state}
            <br />
            <strong>Pincode: </strong>
            {allData.projects.pin_code}
            <br />
          </div>
        </div>

        {/* Summary and Details Section */}
        <div className="section-grid">
          <div>
            <div className="comments-section">
              <div className="comments-title">OTHER DETAILS</div>
              <div className="field-row">
                <span className="field-label">WO Start:</span>
                <span
                  className={`field-value ${
                    !allData.work_order_master.wo_start && "empty"
                  }`}
                >
                  {allData.work_order_master.wo_start
                    ? new Date(
                        allData.work_order_master.wo_start
                      ).toLocaleDateString()
                    : "Not specified"}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">WO End:</span>
                <span
                  className={`field-value ${
                    !allData.work_order_master.wo_end && "empty"
                  }`}
                >
                  {allData.work_order_master.wo_end
                    ? new Date(
                        allData.work_order_master.wo_end
                      ).toLocaleDateString()
                    : "Not specified"}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">No. of Shifts:</span>
                <span className="field-value">
                  {allData.work_order_master.no_shifts || "1"}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">Shift Hours:</span>
                <span className="field-value">
                  {allData.work_order_master.shift_hours || "12"} hrs
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">Monthly Hrs:</span>
                <span className="field-value">
                  {allData.work_order_master.monthly_hrs || "360"} hrs
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">Weekly Off:</span>
                <span
                  className={`field-value ${
                    !allData.work_order_master.weekly_off && "empty"
                  }`}
                >
                  {allData.work_order_master.weekly_off || "Not specified"}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">Include W.Off:</span>
                <span className="field-value">
                  <span
                    className={`boolean-indicator ${allData.work_order_master.include_woff}`}
                  >
                    {allData.work_order_master.include_woff ? "Yes" : "No"}
                  </span>
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">Lunch:</span>
                <span className="field-value">
                  <span
                    className={`boolean-indicator ${allData.work_order_master.lunch}`}
                  >
                    {allData.work_order_master.lunch ? "Yes" : "No"}
                  </span>
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">Sector:</span>
                <span
                  className={`field-value ${
                    !allData.work_order_master.sector_name && "empty"
                  }`}
                >
                  {allData.work_order_master.sector_name || "Not specified"}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">Status:</span>
                <span className="field-value">
                  <span className="status-badge status-open">
                    {allData.work_order_master.wo_open_close || "Open"}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="full-width">
            <div className="section-title">Additional Information</div>
            <div className="field-row">
              <span className="field-label">Kind Attention:</span>
              <span
                className={`field-value ${
                  !allData.work_order_master.kind_attn && "empty"
                }`}
              >
                {allData.work_order_master.kind_attn || "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Amendment No:</span>
              <span
                className={`field-value ${
                  !allData.work_order_master.amendment_no && "empty"
                }`}
              >
                {allData.work_order_master.amendment_no || "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Amendment Date:</span>
              <span
                className={`field-value ${
                  !allData.work_order_master.amendment_date && "empty"
                }`}
              >
                {allData.work_order_master.amendment_date
                  ? new Date(
                      allData.work_order_master.amendment_date
                    ).toLocaleDateString()
                  : "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Demob NIL After:</span>
              <span
                className={`field-value ${
                  !allData.work_order_master.demob_nil_after && "empty"
                }`}
              >
                {allData.work_order_master.demob_nil_after
                  ? `${allData.work_order_master.demob_nil_after} days`
                  : "Not specified"}
              </span>
            </div>
            <div className="field-row">
              <span className="field-label">Remarks:</span>
              <span
                className={`field-value ${
                  !allData.work_order_master.remarks && "empty"
                }`}
              >
                {allData.work_order_master.remarks || "No remarks provided"}
              </span>
            </div>
          </div>
        </div>

        {/* Work Details Table */}
        <div className="table-wrapper">
          <table className="details-table">
            <thead>
              <tr>
                <th>SR.</th>
                <th>ASSET NAME</th>
                <th>START DATE</th>
                <th>END DATE</th>
                <th>RATE/HR</th>
                <th>DAILY RATE</th>
                <th>MOB</th>
                <th>DEMOB</th>
                <th>TAX TYPE</th>
                <th>TAX %</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {validTransactions.map((asset, index) => {
                return (
                  <tr key={index}>
                    <td>{asset.wo_sr_no}</td>
                    <td className="text-left">{asset.asset_name}</td>
                    <td>{new Date(asset.start_date).toLocaleDateString()}</td>
                    <td>{new Date(asset.end_date).toLocaleDateString()}</td>
                    <td className="text-right">₹{formatCurrency(asset.rate_per_hr)}</td>
                    <td className="text-right">₹{formatCurrency(asset.daily_rate)}</td>
                    <td className="text-right">₹{formatCurrency(asset.mob)}</td>
                    <td className="text-right">₹{formatCurrency(asset.demob)}</td>
                    <td className="text-center">{asset.tax_type || "N/A"}</td>
                    <td className="text-center">{asset.tax_percentage || 0}%</td>
                    <td className="text-right">₹{formatCurrency(asset.total_amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Financial Summary Section */}
        <div className="financial-summary-section">
          <div className="summary-content">
            <div className="summary-note">
              <strong>Note:</strong> All amounts are in Indian Rupees (₹). The total shown is the sum of all transaction amounts (each transaction amount already includes base amount, mobilization, demobilization, and applicable taxes).
            </div>
            <div className="summary-table-wrapper">
              <table className="summary-table">
                <tbody>
                  <tr className="summary-total">
                    <td className="summary-label"><strong>Grand Total:</strong></td>
                    <td className="summary-value"><strong>₹{formatCurrency(grandTotal)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkOrderView;